import React from 'react';
import './RTButton.css';
import { canAccessMenu, hasPermission } from '../../utils/permissions';
import { useCurrentMenuKey } from '../../hooks/useMenuCatalog';

const VARIANT_CLASS = {
  primary: 'rt-btn-primary',
  secondary: 'rt-btn-secondary',
  success: 'rt-btn-success',
  warning: 'rt-btn-warning',
  danger: 'rt-btn-danger',
  info: 'rt-btn-info',
  outline: 'rt-btn-outline',
  'outline-primary': 'rt-btn-outline-primary',
};

const SIZE_CLASS = {
  large: 'rt-btn-large',
  normal: 'rt-btn-normal',
  small: 'rt-btn-small',
  sm: 'rt-btn-small', // alias used in a few call sites
};

const ICON_SIZE = {
  large: 24,
  normal: 20,
  small: 16,
  sm: 16,
};

const RTButton = ({
  children,
  type = 'button', // "button", "submit", "link", or "icon"
  variant = 'primary',
  size = 'normal',
  style = {},
  className = '',
  disabled = false,
  href,
  onClick,
  icon,
  iconMode = false,
  menuKey,
  permissionKey,
  requiresWrite = false,
  ...props
}) => {
  const currentMenuKey = useCurrentMenuKey();
  const resolvedMenu = menuKey || currentMenuKey;
  if (permissionKey) {
    if (!hasPermission(permissionKey)) {
      return null;
    }
  } else if (requiresWrite && resolvedMenu && !canAccessMenu(resolvedMenu)) {
    return null;
  }

  const isLink = type === 'link';
  const isIcon = type === 'icon';
  const sizeKey = SIZE_CLASS[size] ? size : 'normal';
  const variantClass = VARIANT_CLASS[variant] || VARIANT_CLASS.primary;
  const sizeClass = SIZE_CLASS[sizeKey];

  const classes = [
    'rt-btn',
    variantClass,
    sizeClass,
    isIcon ? 'rt-btn-icon' : '',
    isLink ? 'rt-btn-link' : '',
    iconMode ? 'rt-btn-icon-mode' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (isIcon) {
    const IconComponent = icon || children;
    const iconPx = ICON_SIZE[sizeKey] || ICON_SIZE.normal;
    return (
      <button
        type="button"
        className={classes}
        style={style}
        disabled={disabled}
        onClick={onClick}
        {...props}
      >
        {React.isValidElement(IconComponent)
          ? React.cloneElement(IconComponent, {
              size: iconPx,
              style: { fontSize: iconPx, ...(IconComponent.props.style || {}) },
            })
          : IconComponent}
      </button>
    );
  }

  if (isLink) {
    return (
      <button
        className={classes}
        style={style}
        onClick={disabled ? (e) => e.preventDefault() : onClick}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  }

  return (
    <button
      type={type}
      className={classes}
      style={style}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

export default RTButton;
